import React, {
    useEffect,
    useState
} from "react";

import { Helmet } from "react-helmet-async";

import {
    ArrowRight,
    Calendar,
    Clock,
    Search,
    Leaf
} from "lucide-react";

import { Link } from "react-router-dom";


// const API_URL = "http://localhost:8483";
const API_URL = "https://www.ayushvatika.com";


interface Blog {

    id: number;

    title: string;

    slug: string;

    category: string;

    excerpt: string;

    featured_image?: string;

    image_alt?: string;

    seo_title?: string;

    meta_description?: string;

    focus_keyword?: string;

    short_tail_keywords?: string;

    long_tail_keywords?: string;

    author?: string;

    published_at?: string;

    created_at: string;

}


const BlogPage: React.FC = () => {

    const [blogs, setBlogs] =
        useState<Blog[]>([]);

    const [loading, setLoading] =
        useState(true);

    const [search, setSearch] =
        useState("");

    const [category, setCategory] =
        useState("All");


    // ========================================================
    // FETCH BLOGS
    // ========================================================

    useEffect(() => {

        const fetchBlogs = async () => {

            try {

                setLoading(true);


                const response =
                    await fetch(
                        `${API_URL}/api/blogs`
                    );


                const data =
                    await response.json();


                if (
                    response.ok &&
                    data.success
                ) {

                    setBlogs(
                        data.blogs || []
                    );

                }

            } catch (error) {

                console.error(
                    "Blog fetch error:",
                    error
                );

            } finally {

                setLoading(false);

            }

        };


        fetchBlogs();

    }, []);


    // ========================================================
    // CATEGORIES
    // ========================================================

    const categories = [
        "All",
        ...Array.from(
            new Set(
                blogs
                    .map(
                        blog => blog.category
                    )
                    .filter(Boolean)
            )
        )
    ];


    // ========================================================
    // FILTER
    // ========================================================

    const filteredBlogs =
        blogs.filter(blog => {

            const matchesSearch =
                blog.title
                    .toLowerCase()
                    .includes(
                        search.toLowerCase()
                    ) ||

                blog.excerpt
                    ?.toLowerCase()
                    .includes(
                        search.toLowerCase()
                    );


            const matchesCategory =
                category === "All" ||
                blog.category === category;


            return (
                matchesSearch &&
                matchesCategory
            );

        });


    // ========================================================
    // DATE
    // ========================================================

    const formatDate = (
        date?: string
    ) => {

        if (!date) {
            return "";
        }


        return new Date(
            date
        ).toLocaleDateString(
            "en-IN",
            {
                day: "numeric",
                month: "short",
                year: "numeric"
            }
        );

    };


    // ========================================================
    // IMAGE URL
    // ========================================================

    const getImageUrl = (
        image?: string
    ) => {

        if (!image) {
            return "/placeholder-blog.webp";
        }


        if (
            image.startsWith("http")
        ) {

            return image;

        }


        return `${API_URL}${image}`;

    };


    return (

        <>

            {/* ==================================================
                SEO
            ================================================== */}

            <Helmet>

                <title>
                    Ayurveda Blog | Ayurvedic Herbs,
                    Natural Wellness & Healthy Lifestyle
                    | Ayushvatika
                </title>


                <meta
                    name="description"
                    content="
                        Explore Ayurvedic herbs, natural
                        wellness tips, Ayurvedic lifestyle,
                        healthy digestion, herbal self-care
                        and traditional Ayurveda knowledge
                        with Ayushvatika.
                    "
                />


                <meta
                    name="keywords"
                    content="
                        Ayurveda,
                        Ayurvedic herbs,
                        Ayurvedic wellness,
                        Ayurvedic lifestyle,
                        natural wellness,
                        herbal remedies,
                        Ayurvedic health,
                        Ayurvedic tips,
                        Ayurvedic herbs for wellness,
                        Ayurvedic lifestyle for healthy living
                    "
                />


                <link
                    rel="canonical"
                    href="https://www.ayushvatika.com/blogs"
                />

            </Helmet>



            <main
                className="
                    min-h-screen
                    bg-white
                "
            >


                {/* ==================================================
                    HERO
                ================================================== */}

                <section
                    className="
                        bg-gradient-to-b
                        from-[#f1faef]
                        via-[#f9fcf8]
                        to-white
                        px-4
                        sm:px-6
                        lg:px-8
                        pt-12
                        pb-10
                    "
                >

                    <div
                        className="
                            max-w-5xl
                            mx-auto
                            text-center
                        "
                    >

                        <div
                            className="
                                inline-flex
                                items-center
                                gap-2
                                px-4
                                py-2
                                bg-green-50
                                border
                                border-green-100
                                text-green-800
                                rounded-full
                                text-xs
                                font-bold
                                uppercase
                                tracking-[0.15em]
                            "
                        >

                            <Leaf
                                className="
                                    w-4
                                    h-4
                                "
                            />

                            Ayurveda Journal

                        </div>


                        <h1
                            className="
                                mt-5
                                text-3xl
                                sm:text-4xl
                                lg:text-5xl
                                font-extrabold
                                text-[#19352a]
                                leading-tight
                            "
                        >

                            Ayurveda Blog:
                            <br />

                            <span
                                className="
                                    text-green-700
                                "
                            >
                                Natural Wellness &
                                Ayurvedic Wisdom
                            </span>

                        </h1>


                        <p
                            className="
                                max-w-3xl
                                mx-auto
                                mt-5
                                text-gray-600
                                text-base
                                sm:text-lg
                                leading-7
                            "
                        >

                            Discover practical Ayurvedic
                            wellness tips, traditional herbs,
                            healthy lifestyle habits,
                            mindful eating and natural
                            self-care practices for everyday
                            living.

                        </p>

                    </div>

                </section>



                {/* ==================================================
                    SEARCH + CATEGORY
                ================================================== */}

                <section
                    className="
                        max-w-7xl
                        mx-auto
                        px-4
                        sm:px-6
                        lg:px-8
                        pt-7
                    "
                >

                    <div
                        className="
                            flex
                            flex-col
                            lg:flex-row
                            lg:items-center
                            lg:justify-between
                            gap-4
                        "
                    >

                        {/* SEARCH */}

                        <div
                            className="
                                relative
                                w-full
                                lg:max-w-md
                            "
                        >

                            <Search
                                className="
                                    absolute
                                    left-4
                                    top-1/2
                                    -translate-y-1/2
                                    w-5
                                    h-5
                                    text-gray-400
                                "
                            />


                            <input
                                type="search"
                                value={search}
                                onChange={e =>
                                    setSearch(
                                        e.target.value
                                    )
                                }
                                placeholder="
                                    Search Ayurveda articles...
                                "
                                className="
                                    w-full
                                    pl-12
                                    pr-4
                                    py-3.5
                                    border
                                    border-gray-200
                                    rounded-xl
                                    outline-none
                                    focus:border-green-600
                                    focus:ring-4
                                    focus:ring-green-100
                                    text-sm
                                "
                            />

                        </div>



                        {/* CATEGORIES */}

                        <div
                            className="
                                flex
                                gap-2
                                overflow-x-auto
                                pb-1
                            "
                        >

                            {categories.map(
                                item => (

                                    <button
                                        key={item}
                                        onClick={() =>
                                            setCategory(
                                                item
                                            )
                                        }
                                        className={`
                                            whitespace-nowrap
                                            px-4
                                            py-2.5
                                            rounded-full
                                            text-sm
                                            font-semibold
                                            transition
                                            ${
                                                category === item
                                                    ? `
                                                        bg-green-700
                                                        text-white
                                                      `
                                                    : `
                                                        bg-gray-100
                                                        text-gray-600
                                                        hover:bg-green-50
                                                        hover:text-green-700
                                                      `
                                            }
                                        `}
                                    >

                                        {item}

                                    </button>

                                )
                            )}

                        </div>

                    </div>

                </section>



                {/* ==================================================
                    BLOG GRID
                ================================================== */}

                <section
                    className="
                        max-w-7xl
                        mx-auto
                        px-4
                        sm:px-6
                        lg:px-8
                        py-10
                    "
                >


                    {/* LOADING */}

                    {loading && (

                        <div
                            className="
                                grid
                                grid-cols-1
                                md:grid-cols-2
                                lg:grid-cols-3
                                gap-7
                            "
                        >

                            {[1, 2, 3, 4, 5, 6]
                                .map(item => (

                                    <div
                                        key={item}
                                        className="
                                            rounded-2xl
                                            bg-gray-100
                                            h-[430px]
                                            animate-pulse
                                        "
                                    />

                                ))}

                        </div>

                    )}



                    {/* BLOGS */}

                    {!loading &&
                        filteredBlogs.length > 0 && (

                            <div
                                className="
                                    grid
                                    grid-cols-1
                                    md:grid-cols-2
                                    lg:grid-cols-3
                                    gap-7
                                "
                            >

                                {filteredBlogs.map(
                                    blog => (

                                        <article
                                            key={blog.id}
                                            className="
                                                group
                                                bg-white
                                                border
                                                border-gray-200
                                                rounded-2xl
                                                overflow-hidden
                                                shadow-sm
                                                hover:shadow-xl
                                                hover:-translate-y-1
                                                transition-all
                                                duration-300
                                                flex
                                                flex-col
                                            "
                                        >

                                            {/* IMAGE */}

                                            <Link
                                                to={`/blogs/${blog.slug}`}
                                                className="
                                                    relative
                                                    block
                                                    aspect-[3/2]
                                                    overflow-hidden
                                                "
                                            >

                                                <img
                                                    src={
                                                        getImageUrl(
                                                            blog.featured_image
                                                        )
                                                    }
                                                    alt={
                                                        blog.image_alt ||
                                                        blog.title
                                                    }
                                                    loading="lazy"
                                                    decoding="async"
                                                    className="
                                                        w-full
                                                        h-full
                                                        object-cover
                                                        group-hover:scale-105
                                                        transition-transform
                                                        duration-700
                                                    "
                                                />


                                                <div
                                                    className="
                                                        absolute
                                                        top-4
                                                        left-4
                                                        bg-white/95
                                                        text-green-800
                                                        px-3
                                                        py-1.5
                                                        rounded-full
                                                        text-[10px]
                                                        font-bold
                                                        uppercase
                                                        tracking-wider
                                                        shadow-sm
                                                    "
                                                >
                                                    {
                                                        blog.category ||
                                                        "Ayurveda"
                                                    }
                                                </div>

                                            </Link>



                                            {/* CONTENT */}

                                            <div
                                                className="
                                                    p-5
                                                    sm:p-6
                                                    flex
                                                    flex-col
                                                    flex-1
                                                "
                                            >

                                                <div
                                                    className="
                                                        flex
                                                        items-center
                                                        gap-4
                                                        text-xs
                                                        text-gray-400
                                                        mb-3
                                                    "
                                                >

                                                    <span
                                                        className="
                                                            inline-flex
                                                            items-center
                                                            gap-1.5
                                                        "
                                                    >

                                                        <Calendar
                                                            className="
                                                                w-3.5
                                                                h-3.5
                                                            "
                                                        />

                                                        {formatDate(
                                                            blog.published_at
                                                        )}

                                                    </span>


                                                    <span
                                                        className="
                                                            inline-flex
                                                            items-center
                                                            gap-1.5
                                                        "
                                                    >

                                                        <Clock
                                                            className="
                                                                w-3.5
                                                                h-3.5
                                                            "
                                                        />

                                                        6 min read

                                                    </span>

                                                </div>



                                                <h2
                                                    className="
                                                        text-xl
                                                        font-bold
                                                        text-[#19352a]
                                                        leading-snug
                                                        line-clamp-2
                                                        group-hover:text-green-700
                                                        transition
                                                    "
                                                >

                                                    <Link
                                                        to={`/blogs/${blog.slug}`}
                                                    >
                                                        {blog.title}
                                                    </Link>

                                                </h2>



                                                <p
                                                    className="
                                                        mt-3
                                                        text-sm
                                                        text-gray-600
                                                        leading-6
                                                        line-clamp-3
                                                    "
                                                >

                                                    {
                                                        blog.excerpt
                                                    }

                                                </p>



                                                <div
                                                    className="
                                                        mt-auto
                                                        pt-5
                                                        border-t
                                                        border-gray-100
                                                    "
                                                >

                                                    <Link
                                                        to={`/blogs/${blog.slug}`}
                                                        className="
                                                            inline-flex
                                                            items-center
                                                            gap-2
                                                            text-green-700
                                                            font-bold
                                                            text-sm
                                                        "
                                                    >

                                                        Read Full Article

                                                        <ArrowRight
                                                            className="
                                                                w-4
                                                                h-4
                                                                group-hover:translate-x-1
                                                                transition
                                                            "
                                                        />

                                                    </Link>

                                                </div>

                                            </div>

                                        </article>

                                    )
                                )}

                            </div>

                        )}



                    {/* NO BLOG */}

                    {!loading &&
                        filteredBlogs.length === 0 && (

                            <div
                                className="
                                    text-center
                                    py-20
                                "
                            >

                                <Leaf
                                    className="
                                        mx-auto
                                        w-12
                                        h-12
                                        text-green-600
                                    "
                                />


                                <h2
                                    className="
                                        mt-4
                                        text-2xl
                                        font-bold
                                        text-gray-800
                                    "
                                >
                                    No Articles Found
                                </h2>


                                <p
                                    className="
                                        text-gray-500
                                        mt-2
                                    "
                                >
                                    Try another search
                                    or category.
                                </p>

                            </div>

                        )}

                </section>



                {/* ==================================================
                    SEO CONTENT
                ================================================== */}

                <section
                    className="
                        bg-[#f7fbf5]
                        border-t
                        border-gray-100
                        py-12
                    "
                >

                    <div
                        className="
                            max-w-4xl
                            mx-auto
                            px-4
                            text-center
                        "
                    >

                        <h2
                            className="
                                text-2xl
                                sm:text-3xl
                                font-bold
                                text-[#19352a]
                            "
                        >
                            Learn Ayurveda for Everyday
                            Wellness
                        </h2>


                        <p
                            className="
                                mt-4
                                text-gray-600
                                leading-7
                            "
                        >

                            Explore our collection of
                            Ayurvedic articles covering
                            Ayurvedic herbs, traditional
                            wellness practices, healthy
                            lifestyle habits, mindful eating,
                            digestive wellness and natural
                            self-care. Our Ayurveda blog is
                            designed to make traditional
                            wellness knowledge easier to
                            understand and apply in everyday
                            life.

                        </p>

                    </div>

                </section>

            </main>

        </>

    );

};


export default BlogPage;